/* -----------------Globals--------------- */
import React from "react";
import {
	Keyboard,
	type KeyboardEvent,
	ScrollView,
	type ScrollViewProps,
} from "react-native";

/* -----------------Props--------------- */
type Props = {
	children: React.ReactNode;
	scrollPosition?: number;
} & ScrollViewProps;

/* -----------------Component--------------- */
const CustomKeyboardAvoidingView = React.forwardRef<ScrollView, Props>(
	(props, ref) => {
		const { children, scrollPosition, ...rest } = props;

		const onKeyboardDidShow = React.useCallback(
			(event: KeyboardEvent) => {
				const keyboardHeight = event.endCoordinates.height;
				(ref as React.RefObject<ScrollView>).current?.scrollTo({
					x: 0,
					y: scrollPosition ? scrollPosition : keyboardHeight + 100,
					animated: true,
				});
			},
			[scrollPosition, ref],
		);

		const onKeyboardDidHide = React.useCallback(() => {
			(ref as React.RefObject<ScrollView>).current?.scrollTo({
				x: 0,
				y: 0,
				animated: true,
			});
		}, [ref]);

		React.useEffect(() => {
			const keyboardDidShowListener = Keyboard.addListener(
				"keyboardDidShow",
				onKeyboardDidShow,
			);
			const keyboardDidHideListener = Keyboard.addListener(
				"keyboardDidHide",
				onKeyboardDidHide,
			);

			return () => {
				keyboardDidShowListener.remove();
				keyboardDidHideListener.remove();
			};
		}, [onKeyboardDidShow, onKeyboardDidHide]);

		return (
			<ScrollView
				contentContainerStyle={{
					flexGrow: 1,
				}}
				ref={ref}
				keyboardShouldPersistTaps="always"
				scrollToOverflowEnabled
				{...rest}
			>
				{children}
			</ScrollView>
		);
	},
);

export default CustomKeyboardAvoidingView;
